import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target,
  Plus,
  CheckCircle2,
  Circle,
  Calendar,
  Edit,
  Trash2,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { Input, Textarea } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { api } from '@/services/client';
import { toast } from 'sonner';

interface Goal {
  id: string;
  title: string;
  description: string;
  targetValue: number;
  currentValue: number;
  unit: string;
  deadline: string;
  status: 'active' | 'completed' | 'overdue';
  category: string;
  milestones: Milestone[];
  createdAt: string;
}

interface Milestone {
  id: string;
  title: string;
  value: number;
  completed: boolean;
  completedAt?: string;
}

function mapMilestones(raw: unknown): Milestone[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((m) => {
    const row = m as Record<string, unknown>;
    return {
      id: String(row.id ?? ''),
      title: String(row.title ?? ''),
      value: Number(row.target_value ?? row.value ?? 0),
      completed: Boolean(row.is_reached ?? row.completed ?? false),
      completedAt: row.reached_at != null ? String(row.reached_at) : undefined,
    };
  });
}

function mapGoalFromApi(g: Record<string, unknown>): Goal {
  const rawStatus = String(g.status ?? 'active');
  const status: Goal['status'] =
    rawStatus === 'completed' ? 'completed' : rawStatus === 'failed' ? 'overdue' : 'active';
  return {
    id: String(g.id ?? ''),
    title: String(g.title ?? ''),
    description: String(g.description ?? ''),
    targetValue: Number(g.target_value ?? g.targetValue ?? 0),
    currentValue: Number(g.current ?? g.currentValue ?? 0),
    unit: String(g.target_unit ?? g.unit ?? ''),
    deadline: String(g.deadline ?? g.end_date ?? ''),
    status,
    category: String(g.category ?? 'General'),
    milestones: mapMilestones(g.milestones),
    createdAt: String(g.created_at ?? g.createdAt ?? ''),
  };
}

export const GoalsPage = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Goal | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    targetValue: 100,
    unit: 'hours',
    deadline: '',
    category: 'Productivity',
  });
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    targetValue: 100,
    unit: 'hours',
    deadline: '',
    category: 'Productivity',
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await api.get<{ goals: Record<string, unknown>[] }>('/goals');
      const mapped = (response.data.goals ?? []).map(mapGoalFromApi);
      setGoals(mapped);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
      toast.error('Failed to load goals');
    }
  };

  const getProgress = (goal: Goal) => {
    if (goal.targetValue <= 0) return goal.status === 'completed' ? 100 : 0;
    return Math.min(100, (goal.currentValue / goal.targetValue) * 100);
  };

  const getDaysRemaining = (deadline: string) => {
    if (!deadline) return 0;
    const now = new Date();
    const end = new Date(deadline);
    const diff = end.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const filteredGoals = goals.filter((goal) => {
    if (filter === 'all') return true;
    if (filter === 'active') return goal.status === 'active' || goal.status === 'overdue';
    return goal.status === 'completed';
  });

  const createGoal = async () => {
    if (!newGoal.title.trim()) return;
    try {
      await api.post('/goals', {
        title: newGoal.title.trim(),
        description: newGoal.description.trim(),
        target_value: newGoal.targetValue,
        target_unit: newGoal.unit,
        end_date: newGoal.deadline || new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
        category: newGoal.category,
      });
      setShowCreateModal(false);
      setNewGoal({ title: '', description: '', targetValue: 100, unit: 'hours', deadline: '', category: 'Productivity' });
      await fetchGoals();
      toast.success('Goal created');
    } catch (error) {
      console.error('Failed to create goal:', error);
      toast.error('Failed to save goal — try again after refreshing');
    }
  };

  const openEditModal = (goal: Goal) => {
    setEditingGoal(goal);
    setEditForm({
      title: goal.title,
      description: goal.description,
      targetValue: goal.targetValue,
      unit: goal.unit,
      deadline: goal.deadline ? goal.deadline.slice(0, 10) : '',
      category: goal.category,
    });
  };

  const saveEdit = async () => {
    if (!editingGoal || !editForm.title.trim()) return;
    try {
      await api.put(`/goals/${editingGoal.id}`, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        end_date: editForm.deadline,
      });
      setEditingGoal(null);
      await fetchGoals();
      toast.success('Goal updated');
    } catch (error) {
      console.error('Failed to update goal:', error);
      toast.error('Failed to update goal');
    }
  };

  const deleteGoal = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/goals/${deleteTarget.id}`);
      setDeleteTarget(null);
      await fetchGoals();
      toast.success('Goal deleted');
    } catch (error) {
      console.error('Failed to delete goal:', error);
      toast.error('Failed to delete goal');
    }
  };

  const toggleGoalComplete = async (goal: Goal) => {
    try {
      if (goal.status === 'completed') {
        await api.post(`/goals/${goal.id}/reopen`);
        toast.success('Goal reopened');
      } else {
        await api.post(`/goals/${goal.id}/complete`);
        toast.success('Goal completed!');
      }
      await fetchGoals();
    } catch (error) {
      console.error('Failed to update goal status:', error);
      toast.error('Failed to update goal');
    }
  };

  const logProgress = async (goal: Goal, increment = 1) => {
    try {
      const res = await api.post<{ status: string }>(`/goals/${goal.id}/progress`, { increment });
      if (res.data.status === 'completed') {
        toast.success('Goal completed!');
      } else {
        toast.success(`Progress logged (+${increment} ${goal.unit})`);
      }
      await fetchGoals();
    } catch (error) {
      console.error('Failed to log progress:', error);
      toast.error('Failed to log progress');
    }
  };

  return (
    <PageLayout title="Goals" description="Set productivity goals and track your progress" icon={Target}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-2xl p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-all shadow-lg flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Goal
            </motion.button>
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            {(['all', 'active', 'completed'] as const).map((f) => (
              <motion.button
                key={f}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  filter === f
                    ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                    : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/20'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </motion.button>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <div className="p-4 bg-info-500/10 border border-info-500/20 rounded-xl">
              <div className="text-info-400 text-sm font-medium mb-1">Total Goals</div>
              <div className="text-3xl font-bold text-khiimori-text-primary">{goals.length}</div>
            </div>
            <div className="p-4 bg-success-500/10 border border-success-500/20 rounded-xl">
              <div className="text-success-400 text-sm font-medium mb-1">Active</div>
              <div className="text-3xl font-bold text-khiimori-text-primary">
                {goals.filter(g => g.status === 'active').length}
              </div>
            </div>
            <div className="p-4 bg-primary-500/10 border border-primary-500/20 rounded-xl">
              <div className="text-primary-400 text-sm font-medium mb-1">Completed</div>
              <div className="text-3xl font-bold text-khiimori-text-primary">
                {goals.filter(g => g.status === 'completed').length}
              </div>
            </div>
            <div className="p-4 bg-warning-500/10 border border-warning-500/20 rounded-xl">
              <div className="text-warning-400 text-sm font-medium mb-1">Completion Rate</div>
              <div className="text-3xl font-bold text-khiimori-text-primary">
                {goals.length > 0 ? Math.round((goals.filter(g => g.status === 'completed').length / goals.length) * 100) : 0}%
              </div>
            </div>
          </div>
        </motion.div>

        {/* Goals Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AnimatePresence mode="popLayout">
            {filteredGoals.map((goal, index) => (
              <motion.div
                key={goal.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05 }}
                className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-2xl p-6 hover:border-khiimori-border/20 transition-all"
              >
                {/* Goal Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => toggleGoalComplete(goal)}
                        title={goal.status === 'completed' ? 'Reopen goal' : 'Mark complete'}
                        className="shrink-0 rounded-full transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      >
                        {goal.status === 'completed' ? (
                          <CheckCircle2 className="w-6 h-6 text-success-400" />
                        ) : (
                          <Circle className="w-6 h-6 text-primary-400 hover:text-success-400" />
                        )}
                      </button>
                      <h3 className={`text-xl font-bold text-khiimori-text-primary ${goal.status === 'completed' ? 'line-through opacity-70' : ''}`}>
                        {goal.title}
                      </h3>
                    </div>
                    <p className="text-sm text-khiimori-text-muted">{goal.description}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-khiimori-text-secondary">Progress</span>
                    <span className="text-sm font-bold text-primary-400">{getProgress(goal).toFixed(0)}%</span>
                  </div>
                  <div className="w-full h-3 bg-khiimori-bg-elevated/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${getProgress(goal)}%` }}
                      transition={{ duration: 1, delay: index * 0.1 }}
                      className={`h-full rounded-full ${goal.status === 'completed' ? 'bg-gradient-to-r from-success-500 to-success-400' : 'bg-gradient-to-r from-primary-500 to-primary-400'}`}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-khiimori-text-muted">
                      {goal.currentValue} / {goal.targetValue} {goal.unit}
                    </span>
                    <span className="text-xs text-khiimori-text-muted">
                      {Math.max(0, goal.targetValue - goal.currentValue)} {goal.unit} remaining
                    </span>
                  </div>
                  {goal.status !== 'completed' ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button variant="glass" size="sm" onClick={() => logProgress(goal, 1)}>
                        +1 {goal.unit}
                      </Button>
                      <Button
                        variant="glass"
                        size="sm"
                        onClick={() => logProgress(goal, Math.max(1, goal.targetValue - goal.currentValue))}
                      >
                        Mark done
                      </Button>
                    </div>
                  ) : null}
                </div>

                {/* Milestones */}
                {goal.milestones.length > 0 ? (
                <div className="mb-4">
                  <div className="text-sm font-medium text-khiimori-text-secondary mb-2">Milestones</div>
                  <div className="space-y-2">
                    {goal.milestones.map((milestone) => (
                      <div
                        key={milestone.id}
                        className={`flex items-center gap-2 p-2 rounded-lg ${
                          milestone.completed ? 'bg-success-500/10' : 'bg-khiimori-bg-elevated/5'
                        }`}
                      >
                        {milestone.completed ? (
                          <CheckCircle2 className="w-4 h-4 text-success-400 flex-shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-khiimori-text-muted flex-shrink-0" />
                        )}
                        <span className={`text-sm flex-1 ${
                          milestone.completed ? 'text-success-400 line-through' : 'text-khiimori-text-secondary'
                        }`}>
                          {milestone.title}
                        </span>
                        {milestone.completedAt && (
                          <span className="text-xs text-success-400">
                            {new Date(milestone.completedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                ) : null}

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-khiimori-border/10">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 text-sm text-khiimori-text-muted">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {goal.status === 'completed' ? (
                          'Completed'
                        ) : (
                          <>
                            {getDaysRemaining(goal.deadline)} days left
                          </>
                        )}
                      </span>
                    </div>
                    <div className="px-2 py-1 bg-primary-500/20 text-primary-400 border border-primary-500/30 rounded text-xs font-medium">
                      {goal.category}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      title="Edit goal"
                      onClick={() => openEditModal(goal)}
                      className="p-2 hover:bg-khiimori-bg-elevated/10 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4 text-khiimori-text-muted" />
                    </button>
                    <button
                      type="button"
                      title="Delete goal"
                      onClick={() => setDeleteTarget(goal)}
                      className="p-2 hover:bg-error-500/10 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-error-400" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {filteredGoals.length === 0 && (
            <div className="col-span-2 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-2xl p-12 text-center">
              <Target className="w-16 h-16 text-khiimori-text-muted mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-khiimori-text-primary mb-2">No Goals Yet</h3>
              <p className="text-khiimori-text-muted mb-6">
                Start setting goals to track your productivity and achievements
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-all shadow-lg"
              >
                Create Your First Goal
              </motion.button>
            </div>
          )}
        </div>

        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="New Goal"
          description="Set a target and deadline to track your progress."
          size="md"
          footer={
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" className="flex-1" onClick={createGoal} disabled={!newGoal.title.trim()}>
                Create Goal
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <Input
              label="Goal title"
              placeholder="e.g. Read 5 articles on ML this week"
              value={newGoal.title}
              onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
            />
            <Textarea
              label="Description"
              placeholder="What does success look like?"
              value={newGoal.description}
              onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
              className="min-h-[88px]"
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Target"
                type="number"
                min={1}
                value={newGoal.targetValue}
                onChange={(e) => setNewGoal({ ...newGoal, targetValue: parseInt(e.target.value) || 0 })}
              />
              <Input
                label="Unit"
                placeholder="hours, tasks…"
                value={newGoal.unit}
                onChange={(e) => setNewGoal({ ...newGoal, unit: e.target.value })}
              />
            </div>
            <Input
              label="Deadline"
              type="date"
              value={newGoal.deadline}
              onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
            />
          </div>
        </Modal>

        <Modal
          isOpen={editingGoal !== null}
          onClose={() => setEditingGoal(null)}
          title="Edit Goal"
          size="md"
          footer={
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => setEditingGoal(null)}>
                Cancel
              </Button>
              <Button variant="primary" className="flex-1" onClick={saveEdit} disabled={!editForm.title.trim()}>
                Save changes
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <Input
              label="Goal title"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
            />
            <Textarea
              label="Description"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              className="min-h-[88px]"
            />
            <Input
              label="Deadline"
              type="date"
              value={editForm.deadline}
              onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })}
            />
          </div>
        </Modal>

        <ConfirmDialog
          isOpen={deleteTarget !== null}
          onClose={() => setDeleteTarget(null)}
          onConfirm={deleteGoal}
          title="Delete goal?"
          message={`"${deleteTarget?.title ?? 'This goal'}" will be permanently removed.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />
      </div>
    </PageLayout>
  );
};
